// angular
import { Component, inject, input, Input, OnChanges } from '@angular/core';
import {
  FormBuilder,
  FormControlOptions,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
// components
import { StagedInputComponent } from '@app/core/components/staged-input/staged-input.component';

@Component({
  selector: 'pwj-climatic-filters-box',
  standalone: true,
  imports: [
    StagedInputComponent,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './climatic-filters-box.component.html',
  styleUrl: './climatic-filters-box.component.scss',
})
export class ClimaticFiltersBoxComponent implements OnChanges {
  public isBoxOpened: boolean = false;
  public climaticFiltersForm!: FormGroup;
  public filterInputs: any = {};
  public currentParams: Params = {};
  public currentFilters!: any;
  public queryParams!: Params;

  // inputs
  boxTitle = input.required<string>();
  boxIcon = input.required<string>();
  isClosableBox = input<boolean>(true);
  filtersType = input.required<'checkbox' | 'staged' | 'range'>();
  climaticFilters = input.required<any>();
  filterPrefix = input<string>('');
  filterSuffix = input<string>('');
  filterSuffixColor = input<string>('');

  // dependencies
  private readonly _fb = inject(FormBuilder);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);

  constructor() {
    // initialize form fields
    this.climaticFiltersForm = this._fb.group({});

    // get last applied filters
    this._activatedRoute.queryParams.forEach(params => {
      // reset box on param changes
      this.climaticFiltersForm.reset();
      this.isBoxOpened = false;

      // set params
      this.currentParams = params;

      this.ngOnChanges();
    });
  }

  ngOnChanges(): void {
    // set bottom sheet data
    if (this.climaticFilters) {
      // check if any filter already selected then open filter box
      this.boxAlreadyOpen();

      this.filterInputs = this.climaticFilters().filterQueryNames;

      this.filterInputs?.forEach((filter: any) => {
        if (this.climaticFilters().isFilterByIdMode) {
          // get form field value
          const filterValue = this.currentFilters?.includes(filter?.id);
          // create form control
          this.addFormControl(filter?.id, filterValue);
        } else {
          // get form field value
          const filterValue = this.currentFilters?.includes(filter);
          // create form control
          this.addFormControl(filter, filterValue);
        }

        this.climaticFiltersForm.patchValue(this.currentParams);
      });
    }

    this.climaticFiltersForm.valueChanges.subscribe(val => {
      this.onSubmitFilters(val);
    });
  }

  public onSubmitFilters(formData: Params) {
    const filtersData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [
        key,
        value === '' ? null : value,
      ])
    );

    this.applyFilter(filtersData);
  }

  public onRemoveFilters() {
    this.climaticFiltersForm.reset();
  }

  public addFormControl(
    fieldName: string,
    value: any = null,
    validators: ValidatorFn | ValidatorFn[] | FormControlOptions | null = []
  ) {
    this.climaticFiltersForm.addControl(
      fieldName,
      this._fb.control(value, validators)
    );
  }

  public applyFilter(appliedFilters: Params) {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: { take: null, skip: null, ...appliedFilters },
      queryParamsHandling: 'merge',
    });
  }

  public setIsBoxOpened() {
    this.isBoxOpened = !this.isBoxOpened;
  }

  public boxAlreadyOpen() {
    Object.keys(this.currentParams).forEach(item => {
      if (item && this.climaticFilters()?.filterQueryNames.includes(item)) {
        this.isBoxOpened = true;
      }
    });
  }
}
